import { ButtonSecondary, ButtonSize, IconExpand, IconLoader } from '@ui/src';
import { useAppSelector } from '@store';
import { Control } from 'react-hook-form';
import groupBy from 'lodash.groupby';
import { v4 as uuid } from 'uuid';
import {
  AccountsCurrenciesFormValues,
  CorporateFormValues,
  FormValues,
} from '../../../hoc/new-application/types';
import StepPartBlock from '../common/step-part-block';
import AccountCurrency from '../common/questionnarie/account-currency';
import AccountFlow from '../common/questionnarie/account-flow';
import ExpectedFundsOrigins from '../common/questionnarie/expected-funds-origins';
import MonthlyVolumes from '../common/monthly-volumes';
import SourceOfFunds from './parts/source-of-funds';
import { IndividualApplicationStepTowTypes } from './types';
import Products from '../common/products';

import commonStyles from '../common/styles.module.css';

const NewApplicationInfoStepTwo = ({
  control,
  status,
  isEditable,
  errors,
  clearErrors,
  questionnarieClearErrors,
  questionnarieControl,
  questionnarieErrors,
  questionnarieSetValue,
  questionnarieFields,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  currencies,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
  customerSourceOfFunds,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  currenciesList,
  regulatedCompanyScheme,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
  countries,
  infoDictionariesIsLoading,
  questionnarieIsLoading,
}: IndividualApplicationStepTowTypes) => {
  const {
    monthlyTurnover,
    numberOfIncomingTransactions,
    numberOfOutgoingTransactions,
    valueOfIncomingTransactions,
    valueOfOutgoingTransactions,
    maxValueOfIncomingPayment,
    maxValueOfOutgoingPayment,
    productsIndividual,
    baseCurrency,
  } = useAppSelector((state) => state.customersDictionaries);

  const groupedAccountsFlows = groupBy(
    accountsFlowsValues,
    (match) => match.currencyId,
  );

  const incomingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'INCOMING',
  );
  const outgoingExpectedFundsOfOrigins = expectedFundsOriginsValues?.filter(
    (item) => item.transactionType === 'OUTGOING',
  );

  return (
    <div className={commonStyles.stepBlockWrapper} style={{ zIndex: 19 }}>
      <div className={commonStyles.stepBlockContent}>
        <MonthlyVolumes
          zIndex={20}
          baseCurrency={baseCurrency}
          control={control as Control<FormValues | CorporateFormValues>}
          clearErrors={clearErrors}
          monthlyTurnover={monthlyTurnover}
          errors={errors}
          status={status}
          isEditable={isEditable}
          numberOfIncomingTransactions={numberOfIncomingTransactions}
          numberOfOutgoingTransactions={numberOfOutgoingTransactions}
          valueOfIncomingTransactions={valueOfIncomingTransactions}
          valueOfOutgoingTransactions={valueOfOutgoingTransactions}
          maxValueOfIncomingPayment={maxValueOfIncomingPayment}
          maxValueOfOutgoingPayment={maxValueOfOutgoingPayment}
          infoDictionariesIsLoading={infoDictionariesIsLoading}
        />
        {regulatedCompanyScheme && (
          <>
            <StepPartBlock
              title="Purpose of establishing a business relationship"
              zIndex={19}
            >
              {questionnarieIsLoading && (
                <div className={commonStyles.dictionaryLoader}>
                  <IconLoader />
                </div>
              )}
              {!questionnarieIsLoading && (
                <div className={commonStyles.accountsCurrenciesList}>
                  {questionnarieErrors?.accountsCurrencies?.message && (
                    <div className={commonStyles.questionnarieError}>
                      {questionnarieErrors?.accountsCurrencies?.message}
                    </div>
                  )}
                  {!accountsCurrenciesIsLoading &&
                    !accountsCurrenciesIsFetching && (
                      <>
                        {!accountsCurrenciesValues?.length && (
                          <div
                            className={commonStyles.emptyAccountsCurrenciesList}
                          >
                            No currencies added yet
                          </div>
                        )}
                        {accountsCurrenciesValues?.map((item, index) => (
                          <AccountCurrency
                            key={item.id}
                            accountCurrency={item}
                            setAccountsCurrenciesValues={
                              setAccountsCurrenciesValues
                            }
                            currencies={currencies}
                            index={index}
                            isEditable={isEditable}
                            status={status}
                            accountsCurrencies={accountsCurrenciesValues}
                            onDeleteAccountCurrencies={
                              onDeleteAccountCurrencies
                            }
                            selectedAccountsCurrencyId={
                              selectedAccountsCurrencyId
                            }
                            deleteAccountsCurrenciesInProgress={
                              deleteAccountsCurrenciesInProgress
                            }
                            setAccountsFlowsValues={setAccountsFlowsValues}
                            accountsFlowsValues={accountsFlowsValues}
                          />
                        ))}
                        {currencies &&
                          currencies?.filter((item) =>
                            accountsCurrenciesValues?.every(
                              (accountCurrency) =>
                                item.value !== accountCurrency.currencyId,
                            ),
                          )?.length > 0 && (
                            <ButtonSecondary
                              text="Add currency"
                              icon={<IconExpand />}
                              className={`button-white ${commonStyles.addAccountCurrencyButton}`}
                              size={ButtonSize.SMALL}
                              onClick={() => {
                                let nextAccountsCurrencies: AccountsCurrenciesFormValues[] =
                                  [];

                                if (accountsCurrenciesValues) {
                                  nextAccountsCurrencies = (
                                    [] as AccountsCurrenciesFormValues[]
                                  ).concat(accountsCurrenciesValues);
                                }

                                nextAccountsCurrencies.push({
                                  id: `accounts-currency-${uuid()}`,
                                  currencyId: '',
                                  plannedTransactionNature: '',
                                  accountOpeningPurpose: '',
                                });

                                setAccountsCurrenciesValues(
                                  nextAccountsCurrencies,
                                );
                              }}
                            />
                          )}
                      </>
                    )}
                  {(accountsCurrenciesIsLoading ||
                    accountsCurrenciesIsFetching) && (
                    <div className={commonStyles.accountsCurrenciesLoader}>
                      <IconLoader />
                    </div>
                  )}
                </div>
              )}
            </StepPartBlock>
            <SourceOfFunds
              questionnarieControl={questionnarieControl}
              customerSourceOfFunds={customerSourceOfFunds}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieFields={questionnarieFields}
              questionnarieSetValue={questionnarieSetValue}
              status={status}
              isEditable={isEditable}
              questionnarieErrors={questionnarieErrors}
              questionnarieIsLoading={questionnarieIsLoading}
            />
            <StepPartBlock title="Expected flows on own account" zIndex={17}>
              {questionnarieIsLoading && (
                <div className={commonStyles.dictionaryLoader}>
                  <IconLoader />
                </div>
              )}
              {!questionnarieIsLoading && (
                <>
                  {!accountsFlowsIsLoading &&
                    !accountsFlowsIsFetching &&
                    accountsFlowsValues &&
                    accountsFlowsValues?.length > 0 && (
                      <div className={commonStyles.expectedFlowsHint}>
                        Currencies selected in the&nbsp;
                        <strong>
                          &quot;Purpose for establishing business
                          relationship&quot;
                        </strong>
                        &nbsp; section
                      </div>
                    )}

                  <div className={commonStyles.accountsCurrenciesList}>
                    {questionnarieErrors?.accountsFlows?.message && (
                      <div className={commonStyles.questionnarieError}>
                        {questionnarieErrors?.accountsFlows?.message}
                      </div>
                    )}
                    {(accountsFlowsIsLoading || accountsFlowsIsFetching) && (
                      <div className={commonStyles.accountsCurrenciesLoader}>
                        <IconLoader />
                      </div>
                    )}
                    {!accountsFlowsValues?.length && (
                      <div className={commonStyles.emptyAccountsFlows}>
                        Add currencies in&nbsp;
                        <strong>
                          &quot;Purpose for establishing business
                          relationship&quot;
                        </strong>
                      </div>
                    )}
                    {!accountsFlowsIsLoading &&
                      !accountsFlowsIsFetching &&
                      Object.keys(groupedAccountsFlows)?.map((group) =>
                        groupedAccountsFlows[group]?.map((flow) => (
                          <AccountFlow
                            key={flow.id}
                            accountFlow={flow}
                            accountsFlows={accountsFlowsValues}
                            setAccountsFlowsValues={setAccountsFlowsValues}
                            currencies={currencies}
                            isEditable={isEditable}
                            status={status}
                            currenciesList={currenciesList}
                          />
                        )),
                      )}
                  </div>
                </>
              )}
            </StepPartBlock>
            <StepPartBlock title="Expected origin of funds" zIndex={16}>
              {questionnarieIsLoading && (
                <div className={commonStyles.dictionaryLoader}>
                  <IconLoader />
                </div>
              )}
              {!questionnarieIsLoading && (
                <div className={commonStyles.accountsCurrenciesList}>
                  {(expectedFundsOriginsIsLoading ||
                    expectedFundsOriginsIsFetching) && (
                    <div className={commonStyles.accountsCurrenciesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {questionnarieErrors?.questionnairesExpectedFundsOrigins
                    ?.message && (
                    <div className={commonStyles.questionnarieError}>
                      {
                        questionnarieErrors?.questionnairesExpectedFundsOrigins
                          ?.message
                      }
                    </div>
                  )}
                  {!accountsFlowsValues?.length && (
                    <div className={commonStyles.emptyAccountsFlows}>
                      Add currencies in&nbsp;
                      <strong>
                        &quot;Purpose for establishing business
                        relationship&quot;
                      </strong>
                    </div>
                  )}

                  {accountsFlowsValues &&
                    accountsFlowsValues?.length > 0 &&
                    !expectedFundsOriginsIsLoading &&
                    !expectedFundsOriginsIsFetching && (
                      <>
                        <ExpectedFundsOrigins
                          status={status}
                          isEditable={isEditable}
                          currencies={
                            accountsCurrenciesValues?.map((item) => ({
                              name: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.name as string,
                              suffix: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.suffix as string,
                              value: item.currencyId,
                            })) || []
                          }
                          deleteExpectedFundsOriginsInProgress={
                            deleteExpectedFundsOriginsInProgress
                          }
                          onDeleteExpectedFundsOrigins={
                            onDeleteExpectedFundsOrigins
                          }
                          selectedExpectedFundsOriginsId={
                            selectedExpectedFundsOriginsId
                          }
                          expectedFundsOriginsValues={
                            incomingExpectedFundsOfOrigins
                          }
                          expectedFundsOriginsValuesList={
                            expectedFundsOriginsValues
                          }
                          setExpectedFundsOriginsValues={
                            setExpectedFundsOriginsValues
                          }
                          countries={countries}
                          type="INCOMING"
                        />
                        <ExpectedFundsOrigins
                          status={status}
                          isEditable={isEditable}
                          currencies={
                            accountsCurrenciesValues?.map((item) => ({
                              name: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.name as string,
                              suffix: currencies?.find(
                                (currency) =>
                                  currency.value === item.currencyId,
                              )?.suffix as string,
                              value: item.currencyId,
                            })) || []
                          }
                          deleteExpectedFundsOriginsInProgress={
                            deleteExpectedFundsOriginsInProgress
                          }
                          onDeleteExpectedFundsOrigins={
                            onDeleteExpectedFundsOrigins
                          }
                          selectedExpectedFundsOriginsId={
                            selectedExpectedFundsOriginsId
                          }
                          expectedFundsOriginsValues={
                            outgoingExpectedFundsOfOrigins
                          }
                          expectedFundsOriginsValuesList={
                            expectedFundsOriginsValues
                          }
                          setExpectedFundsOriginsValues={
                            setExpectedFundsOriginsValues
                          }
                          countries={countries}
                          type="OUTGOING"
                        />
                      </>
                    )}
                </div>
              )}
            </StepPartBlock>
          </>
        )}
        <Products
          control={control as Control<FormValues | CorporateFormValues>}
          products={productsIndividual}
          clearErrors={clearErrors}
          errors={errors}
          status={status}
          isEditable={isEditable}
          zIndex={15}
          infoDictionariesIsLoading={infoDictionariesIsLoading}
        />
      </div>
    </div>
  );
};

export default NewApplicationInfoStepTwo;
